import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addMonths, monthGrid, startOfMonth, toIso, today, type Day } from '../lib/dates';
import { priceOf, type AttributeDef, type Catalogue, type Quote } from '../lib/api';
import {
  arrivalsIn, canArrive, combineFor, covers, departuresFrom, toStays, type Stays,
} from '../lib/stays';
import {
  combinedPeriods, freeOn, possibleArrivals, scanArrivals,
  type AvailabilityCache, type Scan,
} from '../lib/availability';
import { applyFilters, filterableDefs, type FilterState } from '../lib/filters';
import {
  encodeParty, initialParty, partyForAccommodation, type Party,
} from '../lib/party';
import { buildEnquiryLink } from '../lib/link';
import { leaveTo } from '../lib/leave';
import { useI18n } from '../lib/i18n';
import type { EnquiryTarget } from '../components/EnquiryForm';
import type { SearchResult } from './results';
import { useHost } from './WidgetRoot';

/**
 * The search, with no markup in it.
 *
 * ---------------------------------------------------------------------------
 * WHAT COSTS A REQUEST AND WHAT DOES NOT
 * ---------------------------------------------------------------------------
 *
 * Three effects, and the list of what is NOT in their dependencies is the
 * design:
 *
 *   catalogue     once
 *   scan          on (length, month) — never on a filter
 *   quotes        on (arrival, length, party) — never on a filter, and for
 *                 EVERY accommodation rather than the matching ones, so
 *                 loosening a filter reveals a price already held
 *
 * A filter change therefore costs exactly one render. Availability across the
 * filtered set is recomputed by `combinePeriods` from the scan already in
 * memory, which is the closure property §3.3 exists for and the reason this
 * imports the shared function rather than reimplementing the sweep.
 *
 * The scan is the expensive part and it is the API's shape, not a choice:
 * `/api/public/availability` answers one arrival at a time, so a month view is
 * up to 42 requests. `lib/availability.ts` has the detail.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A HOOK AND NOT A COMPONENT
 * ---------------------------------------------------------------------------
 *
 * Because two hosts draw results differently and neither may fork the logic
 * that produces them. See `results.ts`.
 */

type QuoteCache = Map<string, Quote>;

/**
 * Months drawn, and therefore fetched, before the guest scrolls.
 *
 * Six covers the rest of a season and the one after it, which is what a park's
 * own site is asked about, in one request. It grows by six as the guest
 * scrolls.
 */
export const WINDOW_MONTHS = 6;

/**
 * And stops there.
 *
 * DEFECT: this was twelve, and twelve months from an August is the following
 * July. The park published its 2027 rates in August 2026, valid to the end of
 * 2027 — and a guest could not select an arrival past July 2027, so the whole
 * late season was priced, published, available, and unreachable.
 *
 * The horizon a park's own site needs is not "a year from today". It is from
 * today to the far end of what has been published, and a park publishing next
 * season during this one is the normal case rather than the awkward one. From
 * an August that is seventeen months; eighteen covers it with a month spare.
 *
 * Still bounded, and the bound still agrees with the API: the fetch asks for
 * `monthsWanted + 1` months, so nineteen months of window, which is inside
 * `MAX_CALENDAR_DAYS` (600). A park selling beyond that is a conversation, not
 * a scroll.
 */
export const MAX_WINDOW_MONTHS = 18;

function quoteKey(id: string, arrival: Day, nights: number, party: Party): string {
  return `${id}|${arrival}|${nights}|${encodeParty(party)}`;
}

export interface SearchApi {
  catalogue: Catalogue | null;
  /**
   * The cards this widget is actually about, after `scopedTo`.
   *
   * `catalogue.accommodations` is the WHOLE park. A caller asking "what is on
   * offer here" almost always means this instead — the party picker asked for
   * the former and put the campsite's dog on a page selling rental units.
   */
  inScope: readonly Catalogue['accommodations'][number][];
  loadFailed: boolean;
  retry: () => void;

  /** Derived from the dates, never set on its own. */
  nights: number | null;
  /** Moves the DEPARTURE. The chips are an accelerator, not a second truth. */
  setNights: (n: number) => void;
  month: Day;
  setMonth: (m: Day) => void;
  arrival: Day | null;
  setArrival: (d: Day | null) => void;
  departure: Day | null;
  /** Both dates at once: changing one usually changes the other. */
  setDates: (next: { arrival: Day | null; departure: Day | null }) => void;
  /** Dates this arrival may end on. Null until there is one. */
  departures: Set<Day> | null;
  /** First month the calendar draws, and how many. */
  monthsFrom: Day;
  monthCount: number;
  /** The guest is near the end of what is drawn; draw and fetch more. */
  needMonths: () => void;
  /** First day nothing is known about. Cells past it are not drawn as full. */
  unknownBeyond: Day | null;
  /** The filters match nothing — which is not the same as the park being full. */
  emptyFilter: boolean;
  party: Party;
  setParty: (p: Party) => void;
  filters: FilterState;
  setFilters: (f: FilterState) => void;

  /** Today, from the host's clock. Nothing earlier can be arrived on. */
  minDay: Day;
  /** Dates a stay of `nights` can begin on, across the FILTERED set. */
  arrivable: Set<Day> | null;
  scanning: boolean;
  scanFailed: boolean;
  quoting: boolean;

  /** Attributes worth offering a control for. Empty when scoped to one. */
  filterDefs: AttributeDef[];
  /** Every attribute definition, for renderers that display values. */
  allDefs: AttributeDef[];

  results: SearchResult[];
  all: SearchResult[];
  chosen: string | null;
  choose: (accommodationId: string) => void;
}

export function useSearch({ scopedTo, kinds, initial }: {
  /**
   * Narrow to one accommodation, or to a SET of them.
   *
   * One id is the accommodation's own page. A set is the case a large park has
   * and a small one does not: a "caravans" page listing eight of a park's forty
   * types, or a campaign page for three lodges. With only a single id, such a
   * page either lists everything the park sells or needs eight widgets on it.
   *
   * A string stays accepted, because every existing caller passes one and
   * rewriting them into one-element arrays would be churn for its own sake.
   */
  scopedTo?: string | readonly string[];
  /** Narrows to a kind of accommodation. See `WidgetOptions.kinds`. */
  kinds?: readonly string[];
  initial?: { arrival?: Day; nights?: number; party?: Party };
} = {}): SearchApi {
  const host = useHost();
  const { api, onCatalogue } = host;
  // Read here rather than passed in: the language on screen is the one to forward,
  // and the provider is the only thing that knows what it resolved to.
  const { lang } = useI18n();

  // The park's clock decides what "today" is, not the guest's and not UTC's. A
  // guest in Amsterdam at 00:30 is on a new day; UTC still calls it yesterday, and
  // the calendar would offer an arrival that has passed.
  const minDay = useMemo(
    () => today(host.now, host.config.timeZone),
    [host.now, host.config.timeZone],
  );

  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloads, setReloads] = useState(0);

  const [month, setMonth] = useState<Day>(() => startOfMonth(initial?.arrival ?? minDay));
  const [arrival, setArrival] = useState<Day | null>(initial?.arrival ?? null);
  const [party, setParty] = useState<Party>(() => initial?.party ?? initialParty());
  const [filters, setFilters] = useState<FilterState>({});

  /**
   * The departure, from which the length is derived rather than asked for.
   *
   * `nights` is still settable — the length chips are an accelerator, and a
   * WordPress host may pass one in `initial` — and setting it moves the
   * departure. There is one source of truth and it is the pair of dates.
   */
  const [departure, setDeparture] = useState<Day | null>(
    initial?.arrival != null && initial?.nights != null
      ? initial.arrival + initial.nights : null,
  );

  const [stays, setStays] = useState<Stays | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanFailed, setScanFailed] = useState(false);
  /** How many months the calendar has been asked to draw. Grows on scroll. */
  const [monthsWanted, setMonthsWanted] = useState(WINDOW_MONTHS);

  const [free, setFree] = useState<Map<string, number | null>>(() => new Map());
  const [quotes, setQuotes] = useState<Map<string, Quote>>(() => new Map());
  const [quoting, setQuoting] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);

  // Both caches outlive every render and belong to this widget instance, so two
  // widgets on one page never hand each other a stale answer.
  const availabilityCache = useRef<AvailabilityCache>(new Map());
  const quoteCache = useRef<QuoteCache>(new Map());

  /* ------------------------------------------------------------- catalogue */
  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    api.catalogue()
      .then((c) => {
        if (cancelled) return;
        setCatalogue(c);
        // The host gets it too, so the enquiry form does not fetch the country
        // block a second time from one component away.
        onCatalogue?.(c);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [api, onCatalogue, reloads]);

  const everything = useMemo(() => {
    if (!catalogue) return [];
    /*
      Kind first, because it is the coarse half and composes with the other.

      A page about the park's rental units is about those units, and a widget on
      it that answers for the whole park is wrong in ways nobody sees until one
      of them bites: the campsite's dog offered on a lodge page, an age band
      read off a card the guest cannot choose, a scan of accommodations that are
      not on the page.
    */
    const kinded = kinds?.length
      ? catalogue.accommodations.filter((a) => kinds.includes(a.kind))
      : catalogue.accommodations;
    if (!scopedTo) return kinded;
    /**
     * DEFECT: the SET form documented on `scopedTo` did not work.
     *
     * `a.id === scopedTo` compares a string against whatever was passed, so an
     * array matched nothing at all and the widget showed an empty park — the
     * worst possible failure for a page whose whole purpose is to list three
     * lodges. It went unnoticed because both callers in this repo pass a single
     * id, which is precisely the case the comparison happens to handle.
     */
    const wanted = new Set(typeof scopedTo === 'string' ? [scopedTo] : scopedTo);
    return kinded.filter((a) => wanted.has(a.id));
  }, [catalogue, scopedTo, kinds]);

  /**
   * The departure, and the one case where a host supplies it.
   *
   * A page may embed the widget for a fixed length — `?nights=3`, or a
   * WordPress block for weekly rentals — without naming an arrival. That is an
   * instruction about the product, not a guess about the guest, so the length
   * stands in as a departure until the guest picks one of their own. Choosing
   * a new arrival clears the departure and the default applies again, which is
   * what a fixed-length embed should do.
   */
  const effectiveDeparture = departure ?? (
    arrival !== null && initial?.nights ? arrival + initial.nights : null
  );

  /** The length, derived. There is one source of truth and it is the dates. */
  const nights = arrival !== null && effectiveDeparture !== null && effectiveDeparture > arrival
    ? effectiveDeparture - arrival : null;

  /* -------------------------------------------------------------- the window */

  /**
   * One request for the whole horizon, rather than one per arrival date.
   *
   * The old shape asked `/api/public/availability` once per candidate arrival —
   * up to 42 calls for a month, and only affordable once a length was known,
   * which is why the guest used to be asked for a length before seeing a
   * calendar. `/api/public/calendar` returns the compressed periods for a range
   * and every question is then answered locally in `lib/stays.ts`.
   *
   * Refetched only when the guest scrolls past what is held. `covers` is what
   * decides, so a wider window already in hand is never re-requested.
   */
  const wantUntil = useMemo(
    () => addMonths(startOfMonth(minDay), monthsWanted + 1),
    [minDay, monthsWanted],
  );

  /**
   * The range currently in flight.
   *
   * `covers` cannot answer this on its own: it reads `stays`, which is still
   * null while the first request is outstanding, so any re-render in that gap —
   * `setScanning` causes one immediately — sent the identical request a second
   * time. Two calls for one window on every load, with nothing on screen to
   * show for it.
   */
  const inFlight = useRef<string | null>(null);

  useEffect(() => {
    if (!catalogue) return undefined;
    if (covers(stays, minDay, wantUntil)) return undefined;

    const asking = `${minDay}|${wantUntil}`;
    if (inFlight.current === asking) return undefined;
    inFlight.current = asking;

    let cancelled = false;
    setScanning(true);
    setScanFailed(false);
    api.calendar(toIso(minDay), toIso(wantUntil))
      .then((body) => { if (!cancelled) { setStays(toStays(body)); setScanning(false); } })
      .catch(() => {
        if (cancelled) return;
        // Cleared on failure only, so a retry can happen; a success is covered
        // by `stays` and must not be asked for again.
        inFlight.current = null;
        setScanning(false);
        setScanFailed(true);
      });
    return () => { cancelled = true; };
  }, [api, catalogue, minDay, wantUntil, stays]);

  /**
   * Free-unit counts for the stay the guest actually chose.
   *
   * The window route publishes WINDOWS, never counts — which pitch is free is
   * operational detail, and a count per date for a season would be a map of the
   * park's occupancy. But "only 2 left" is worth saying, and it is one request
   * for one stay rather than one per candidate date: this is what the old
   * per-date route is for, now that it is asked once instead of forty-two
   * times.
   *
   * A failure is silent. The tag is a nicety; the price and the availability
   * both come from the quote and neither waits on this.
   */
  useEffect(() => {
    if (arrival === null || nights === null) { setFree(new Map()); return undefined; }
    let cancelled = false;
    api.availability(toIso(arrival), nights)
      .then((a) => {
        if (cancelled) return;
        setFree(new Map(a.accommodations.map((x) => [x.id, x.free])));
      })
      .catch(() => { if (!cancelled) setFree(new Map()); });
    return () => { cancelled = true; };
  }, [api, arrival, nights]);

  /* ---------------------------------------------------------------- quotes */
  useEffect(() => {
    if (arrival === null || nights === null || everything.length === 0) return undefined;
    let cancelled = false;

    // Paint from what is already held before asking again. This is the held
    // table §7 permits: it makes the panel instant and survives a slow API, and
    // the figure shown is still the API's own from a moment ago.
    const held = new Map<string, Quote>();
    for (const acc of everything) {
      const cached = quoteCache.current.get(quoteKey(acc.id, arrival, nights, party));
      if (cached) held.set(acc.id, cached);
    }
    setQuotes(held);
    setQuoting(true);

    void (async () => {
      const batch = 4;
      for (let i = 0; i < everything.length; i += batch) {
        const slice = everything.slice(i, i + batch);
        // eslint-disable-next-line no-await-in-loop
        const answers = await Promise.all(slice.map(async (acc) => {
          const key = quoteKey(acc.id, arrival, nights, party);
          const previous = quoteCache.current.get(key);
          const expected = previous ? priceOf(previous) : null;
          // Per accommodation, because the KEYS are the card's own. One map for
          // the whole list is what sent `adult` to a card that prices by
          // `volwassene`, and got a 400 for every such card on the park.
          const quantities = partyForAccommodation(party, acc.quantities);
          // The card cannot price this party — a dog where there is no dog. Its
          // tile falls back to "ask us", which is the honest answer.
          if (quantities === null) return null;
          try {
            return await api.quote({
              accommodationId: acc.id,
              arrival: toIso(arrival),
              nights,
              quantities,
              // Sent so a DISAGREEMENT is reported. The API never believes it:
              // it recomputes, logs the difference and hands back
              // `priceChanged`, so the guest is told rather than swapped under.
              ...(expected !== null ? { expected } : {}),
            });
          } catch {
            // One accommodation failing to price must not empty the list. Its
            // card falls back to "ask us for a price", which is true and is a
            // route the park is happy to take.
            return null;
          }
        }));
        if (cancelled) return;
        setQuotes((current) => {
          const next = new Map(current);
          slice.forEach((acc, n) => {
            const q = answers[n];
            if (!q) return;
            next.set(acc.id, q);
            quoteCache.current.set(quoteKey(acc.id, arrival, nights, party), q);
          });
          return next;
        });
      }
      if (!cancelled) setQuoting(false);
    })();

    return () => { cancelled = true; };
    // `filters` is deliberately absent — see the header.
  }, [api, arrival, nights, party, everything]);

  /* --------------------------------------------------------------- derived */
  const filterDefs = useMemo(
    () => (catalogue && !scopedTo ? filterableDefs(catalogue.attributes, everything) : []),
    [catalogue, everything, scopedTo],
  );

  const matching = useMemo(() => applyFilters(everything, filters), [everything, filters]);

  /**
   * Which dates the FILTERED set can be arrived on.
   *
   * Pure, and this is the whole trick: `combinePeriods` closes the encoding
   * under union, so the answer for any subset comes out of data already held. A
   * scan taken at another length is discarded rather than reinterpreted — those
   * periods describe one length and lie about every other.
   */
  /** The ids the guest's filters leave, which is what the calendar is about. */
  const matchingIds = useMemo(() => matching.map((a) => a.id), [matching]);

  /**
   * Dates a stay may BEGIN on, across the filtered set.
   *
   * Computed for the months on screen rather than the whole horizon: a year of
   * cells is 365 arrival tests over every type, and the guest is looking at
   * two months of it.
   */
  const arrivable = useMemo(() => {
    if (!stays) return null;
    // A host that fixed the length gets the old question, and should: on an
    // embed selling three-night breaks, a date that cannot take three nights is
    // a date the guest must not be offered. Everywhere else the question is
    // "can any stay begin here", because the guest has not chosen a length and
    // is not going to be asked to.
    const fixed = departure === null ? initial?.nights ?? null : null;
    return arrivalsIn(
      stays, matchingIds, minDay, addMonths(startOfMonth(minDay), monthsWanted + 1), fixed,
    );
  }, [stays, matchingIds, minDay, monthsWanted, departure, initial?.nights]);

  /** Dates this arrival can END on. Null until there is an arrival. */
  const departures = useMemo(() => {
    if (!stays || arrival === null) return null;
    return departuresFrom(stays, matchingIds, arrival);
  }, [stays, matchingIds, arrival]);

  /**
   * Setting a length moves the departure.
   *
   * Kept because the chips are a real accelerator for a guest who thinks in
   * weeks, and because a WordPress host may pass a length in `initial`. It is
   * NOT a second source of truth: it writes the same pair of dates.
   */
  const setNights = useCallback((n: number) => {
    if (arrival === null) return;
    setDeparture(arrival + n);
  }, [arrival]);

  const setDates = useCallback((next: { arrival: Day | null; departure: Day | null }) => {
    setArrival(next.arrival);
    setDeparture(next.departure);
  }, []);

  const all = useMemo<SearchResult[]>(() => {
    const matched = new Set(matching.map((a) => a.id));
    return everything.map((accommodation) => {
      const quote = quotes.get(accommodation.id) ?? null;
      const freeUnits = free.get(accommodation.id) ?? null;
      return {
        accommodation,
        quote,
        price: quote ? priceOf(quote) : null,
        // The server decides how a stay may be had, and refuses to say `direct`
        // where it takes no bookings. Believed, not second-guessed.
        booking: quote?.booking ?? (catalogue?.features.bookings === false ? 'contact' : 'direct'),
        /**
         * THREE STATES ON BOTH SIDES OF THE `?`, which is what this got wrong.
         *
         * `!== false` on the quote is right: an absent `available` means the
         * park enumerates no units of this type, which is "ask us" and not
         * "sold out". But the fallback read `(freeUnits ?? 0) !== 0`, which
         * turns the SAME unknown into a zero and then into "sold out".
         *
         * DEFECT (reported): "adding a child gives no availability", and again
         * for a dog. Both are the no-quote path. This park's camping card
         * declares two guest categories, both mapped to `adult` and both still
         * called `q0`/`q1` from the import — so a child or a dog maps to no
         * category, `partyForAccommodation` declines to invent a price, and no
         * quote is fetched. Correct so far: the comment beside it says the tile
         * then "falls back to ask us, which is the honest answer".
         *
         * It did not. A camping field enumerates no pitches, so `freeUnits` is
         * null, and the fallback answered "NIET VRIJ OP DEZE DATA" — telling a
         * family with a child that a campsite which takes children is full.
         *
         * Null is unknown. Unknown is "ask us".
         */
        available: quote
          ? quote.available !== false
          : (freeUnits === null ? true : freeUnits !== 0),
        free: freeUnits,
        matched: matched.has(accommodation.id),
        loading: quoting && !quote,
      };
    });
  }, [everything, matching, quotes, free, catalogue, quoting]);

  const results = useMemo(() => all
    .filter((r) => r.matched)
    .sort((a, b) => {
      if (a.available !== b.available) return a.available ? -1 : 1;
      if (a.price === null || b.price === null) {
        return a.price === b.price ? 0 : (a.price === null ? 1 : -1);
      }
      return a.price - b.price;
    }), [all]);

  function choose(accommodationId: string) {
    if (arrival === null || nights === null) return;
    const accommodation = everything.find((a) => a.id === accommodationId);
    if (!accommodation) return;
    setChosen(accommodationId);
    const target: EnquiryTarget = {
      accommodation,
      arrival,
      nights,
      party,
      quote: quotes.get(accommodationId) ?? null,
    };
    if (host.handlesSelection) {
      host.onSelect(target);
      return;
    }
    // No handler means we are on somebody else's page: hand the guest over to
    // the app with everything they have already chosen. `buildEnquiryLink` is
    // exported from the widget bundle so the plugin writes the same URL this
    // app parses.
    if (host.enquiryUrl) {
      // Leaves the frame; see `leaveTo` for why that is not `location.href`.
      leaveTo(buildEnquiryLink(host.enquiryUrl, {
        accommodationId,
        arrival,
        nights,
        party,
        parkId: host.config.parkId || undefined,
        // The language travels with the guest.
        //
        // Without it, somebody reading the English version of an accommodation
        // page clicked through and landed on a Dutch enquiry form — because the
        // app then resolved the language from scratch and knew nothing about the
        // page they came from. The language of the page they were on is the best
        // evidence there is, and it arrives as `?lang=`, which is the host stating
        // it and therefore beats the destination's browser.
        lang,
      }));
    }
  }

  /**
   * Draw and fetch further ahead.
   *
   * Bounded, because `MAX_CALENDAR_DAYS` caps a window at 400 days and a
   * calendar that kept growing would eventually ask for one the API refuses.
   * A park selling further out than that is a conversation, not a scroll.
   */
  const needMonths = useCallback(() => {
    setMonthsWanted((n) => (n >= MAX_WINDOW_MONTHS ? n : n + 6));
  }, []);

  /**
   * The filters match nothing.
   *
   * Kept separate from availability all the way to the screen, because the two
   * look identical on a calendar and mean opposite things: a park is not full
   * because somebody ticked a filter, and greying every date to say so is the
   * single most misleading thing this widget could do.
   */
  const emptyFilter = catalogue !== null && everything.length > 0 && matching.length === 0;

  return {
    catalogue,
    /**
     * The cards this widget is actually about, after `scopedTo`.
     *
     * Exposed because `catalogue.accommodations` is the WHOLE park, and a
     * caller that wants "what is on offer here" almost always means this one.
     * The party picker asked for the first and got the park's camping dog on a
     * page selling rental units — see `petLimitFor`.
     */
    inScope: everything,
    loadFailed,
    retry: () => setReloads((n) => n + 1),
    nights,
    setNights,
    month,
    setMonth,
    arrival,
    setArrival,
    departure: effectiveDeparture,
    setDates,
    departures,
    monthsFrom: startOfMonth(minDay),
    monthCount: monthsWanted,
    needMonths,
    unknownBeyond: stays ? stays.until : null,
    emptyFilter,
    party,
    setParty,
    filters,
    setFilters,
    minDay,
    arrivable,
    scanning,
    scanFailed,
    quoting,
    filterDefs,
    allDefs: catalogue?.attributes ?? [],
    results,
    all,
    chosen,
    choose,
  };
}
